import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';

import Validator, { useValidate, checkValidity } from '../../../components/validator';

import Auth from '../../../data/auth-service';
import DataContext from '../../../data/data-context';

const useStyles = makeStyles(theme => ({
    labelSpacing: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
}));

const VPN = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { systemSettings, updateSystemSettings } = useContext(DataContext);
    const [vpn, setVPN] = useState({
        enabled: false,
        fragment: 0,
        has_clients: false,
        max_clients: 111,
        mssfix: 0,
        tunnel_network: '10.11.42.0/24',
    });
    const [original, setOriginal] = useState({});
    const [changes, setChanges] = useState(false);
    const vpnForm = useValidate({});
    const vpnPerms = Auth.permissions.get('VPN');

    useEffect(() => {
        if (systemSettings && systemSettings.vpnServer) {
            const v = _cloneDeep(systemSettings.vpnServer);
            setVPN(v);
            setOriginal(systemSettings.vpnServer);
        }
    }, [systemSettings]);

    useEffect(() => {
        setChanges(_isEqual(vpn, original));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vpn]);

    const handleEnable = (e) => {
        setVPN({ ...vpn, enabled: e.target.checked });
    };

    const handleMaxClients = (e, validID, valid) => {
        vpnForm.set({ ...vpnForm.get(), [validID]: valid });

        setVPN({
            ...vpn,
            max_clients: parseInt(e.target.value)
        });
    };

    const handleTunnelNetwork = (e, validID, valid) => {
        vpnForm.set({ ...vpnForm.get(), [validID]: valid });

        setVPN({ ...vpn, tunnel_network: e.target.value });
    };

    const handleFragment = (e, validID, valid) => {
        vpnForm.set({ ...vpnForm.get(), [validID]: valid });

        setVPN({
            ...vpn,
            fragment: parseInt(e.target.value)
        });
    };

    const handleMSSFix = (e, validID, valid) => {
        vpnForm.set({ ...vpnForm.get(), [validID]: valid });

        setVPN({
            ...vpn,
            mssfix: parseInt(e.target.value)
        });
    };

    const handleSave = () => {
        updateSystemSettings({ vpnServer: vpn });
    };

    const handleCancel = () => {
        navigate('/system/configuration');
    };

    return (
        <Paper elevation={3}>
            <Box p={5}>
                <Grid container spacing={5}>
                    <Grid item xs={12}>
                        <Box>
                            <FormControlLabel
                                disabled={!vpnPerms.admin}
                                aria-label="enabled"
                                label="enabled"
                                control={<Checkbox checked={vpn.enabled} onClick={handleEnable} />}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box>
                            <Validator
                                valueType="range"
                                errorText="Max clients must be within 11 and 1111"
                                type="number"
                                inputProps={{
                                    min: 11,
                                    max: 1111,
                                }}
                                label="Max Clients"
                                aria-label="max clients"
                                fullWidth
                                disabled={!vpnPerms.write || !vpn.enabled}
                                value={vpn.max_clients}
                                onChange={handleMaxClients}
                            />
                            <FormHelperText>Specify the maximum number of clients allowed to concurrently connect to this server.</FormHelperText>
                        </Box>
                        <Box mt={3}>
                            <Validator
                                valueType="cidr"
                                errorText="Tunnel network must be a valid CIDR range"
                                label="Tunnel Network"
                                aria-label="tunnel network"
                                fullWidth
                                disabled={!vpnPerms.write || !vpn.enabled}
                                value={vpn.tunnel_network}
                                onChange={handleTunnelNetwork}
                            />
                            <FormHelperText>IPv4 network used for communication between this server and client. This value can only be changed when no sensors are registered within this subnet.</FormHelperText>
                            <FormHelperText className={classes.labelSpacing}>Making changes to this page will cause the server to restart and all clients must reconnect!</FormHelperText>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box>
                            <Validator
                                valueType="range"
                                errorText="Fragment value must be within 0 and 1500"
                                type="number"
                                inputProps={{
                                    min: 0,
                                    max: 1500,
                                }}
                                label="Fragment"
                                aria-label="fragment"
                                fullWidth
                                disabled={!vpnPerms.write || !vpn.enabled}
                                value={vpn.fragment}
                                onChange={handleFragment}
                            />
                            <FormHelperText>
                                Enable internal datagram fragmentation so that no UDP datagrams are sent which are larger than the specified size in bytes.
                                Valid range is 0-1500. A value of 0 means this value is unset. This value can only be changed when no sensors are registered
                                within this subnet. The value must be the same on all clients and the server, it is sent to a client when the initial
                                connection is made.
                            </FormHelperText>
                        </Box>
                        <Box mt={3}>
                            <Validator
                                valueType="range"
                                errorText="MSSFix value must be within 0 and 1500"
                                type="number"
                                inputProps={{
                                    min: 0,
                                    max: 1500,
                                }}
                                label="MSSFix"
                                aria-label="mssfix"
                                fullWidth
                                disabled={!vpnPerms.write || !vpn.enabled}
                                value={vpn.mssfix}
                                onChange={handleMSSFix}
                            />
                            <FormHelperText>
                                Accounce to TCP sessions running over the tunnel that they should limit their send packet sizes such that after OpenVPN has
                                encapsulated them, the resulting UDP packet size that OpenVPN sends to its peer will not exceed the specified size in bytes.
                                Valid range is 0-1500. A value of 0 means this value is unset.
                            </FormHelperText>
                        </Box>
                    </Grid>
                    {(vpnPerms.write || vpnPerms.admin) && <Grid item xs={12}>
                        <Box mt={3} mb={5}>
                            <Button
                                className={classes.buttonRight}
                                variant="contained"
                                color="primary"
                                aria-label="save"
                                onClick={handleSave}
                                disabled={!(checkValidity(vpnForm.get()) && !changes)}
                            >
                                Save
                            </Button>
                            <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={handleCancel}>Cancel</Button>
                        </Box>
                    </Grid>}
                </Grid>
            </Box>
        </Paper>
    );
};

export default VPN;
